"use client";

import * as React from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

import { cn } from "@/app/_lib/utils";
import { trackEvent } from "@/app/_lib/analytics";

/**
 * Case-study sticky bottom bar. Hidden at the top; slides in once the user
 * scrolls past the Overview section (observes `sentinelId`, which sits at the
 * end of the Overview block).
 */
export function CaseStudyStickyBar({
  name,
  sentinelId,
}: {
  name: string;
  sentinelId: string;
}) {
  const [pastOverview, setPastOverview] = React.useState(false);
  const [footerVisible, setFooterVisible] = React.useState(false);

  React.useEffect(() => {
    const el = document.getElementById(sentinelId);
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        setPastOverview(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [sentinelId]);

  // Hide the bar once the site footer scrolls into view so it never overlaps
  // the copyright row at the bottom of the page.
  React.useEffect(() => {
    const footer = document.getElementById("site-footer");
    if (!footer) return;
    const io = new IntersectionObserver(([entry]) => setFooterVisible(entry.isIntersecting), {
      threshold: 0,
    });
    io.observe(footer);
    return () => io.disconnect();
  }, []);

  const show = pastOverview && !footerVisible;

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 transition-transform duration-500 ease-out",
        show ? "translate-y-0" : "translate-y-full",
      )}
    >
      <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4 border-t border-border bg-background/90 px-6 py-3 backdrop-blur md:px-[100px]">
        <span className="text-sm font-medium text-muted-foreground">
          {name} <span className="text-muted-foreground/60">– Case Study</span>
        </span>
        <Link
          href="/contact-us"
          onClick={() => trackEvent("start_project_click", { location: "portfolio_sticky_bar" })}
          className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
        >
          Start a project <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
